// JQ4-45 · US-AUDIT-005 — Audit Log API client
//
// Provides typed wrappers for both audit-log endpoints:
//   - listAuditLog()        → GET /admin/audit-log           (org admin)
//   - listOrgAuditLog()     → GET /super-admin/organizations/:orgId/audit-log (super admin)
//
// Mirrors the shape of ticket-history.ts but with the full filter surface from
// ListAuditLogQuerySchema (cursor, limit, from, to, category, actorUserId).

import type { AuditCategory, ListAuditLogQuery, ListAuditLogResponse } from '@jq4race/shared';

import { request } from './client.js';

// Re-export so consumers only need one import
export type { AuditCategory };

// ── Query param builder ────────────────────────────────────────────────────────

function buildQueryString(params: ListAuditLogQuery): string {
  const entries: [string, string][] = [];

  if (params.cursor) entries.push(['cursor', params.cursor]);
  if (params.limit !== undefined) entries.push(['limit', String(params.limit)]);
  if (params.from) entries.push(['from', params.from]);
  if (params.to) entries.push(['to', params.to]);
  if (params.category) entries.push(['category', params.category]);
  if (params.actorUserId) entries.push(['actorUserId', params.actorUserId]);
  if (params.action) entries.push(['action', params.action]);
  if (params.resourceId) entries.push(['resourceId', params.resourceId]);

  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`;
}

// ── Org Admin variant ─────────────────────────────────────────────────────────

/**
 * GET /admin/audit-log
 *
 * Returns a cursor-paginated list of audit events scoped to the calling
 * org_admin's organization (RLS-enforced on the server).
 */
export async function listAuditLog(params: ListAuditLogQuery = {}): Promise<ListAuditLogResponse> {
  return request<ListAuditLogResponse>(`/admin/audit-log${buildQueryString(params)}`, {
    method: 'GET',
  });
}

// ── Super Admin variant ───────────────────────────────────────────────────────

/**
 * GET /super-admin/organizations/:orgId/audit-log
 *
 * Returns a cursor-paginated list of audit events for the specified organization.
 * Requires super_admin role. The actor picker for super-admin is deferred (see R1
 * scope guard in design: no cross-org user-list endpoint exists). actorUserId can
 * still be passed as a raw UUID if known.
 */
export async function listOrgAuditLog(
  orgId: string,
  params: ListAuditLogQuery = {},
): Promise<ListAuditLogResponse> {
  return request<ListAuditLogResponse>(
    `/super-admin/organizations/${encodeURIComponent(orgId)}/audit-log${buildQueryString(params)}`,
    { method: 'GET' },
  );
}

// ── Platform (cross-org) variant ─────────────────────────────────────────────

/**
 * GET /super-admin/audit-log
 *
 * role-based-dashboards — Phase 4. Returns a cursor-paginated, CROSS-ORG audit
 * feed (no `orgId` scoping) — the same filter surface as `listAuditLog`,
 * restricted to `super_admin` (server-enforced via
 * `UnitOfWork.runAsPlatformAdmin`, see design "RLS cross-org bypass").
 */
export async function listPlatformAuditLog(
  params: ListAuditLogQuery = {},
): Promise<ListAuditLogResponse> {
  return request<ListAuditLogResponse>(`/super-admin/audit-log${buildQueryString(params)}`, {
    method: 'GET',
  });
}
